"use client";

import { useEffect, useRef } from "react";

const ENDPOINT = "/api/v1/system/client-log";

interface LogQueueItem {
  type: "ERROR" | "WARN" | "INFO";
  message: string;
  stack?: string;
  timestamp: string;
  url: string;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split("; ")
    .find((chunk) => chunk.startsWith(`${name}=`));
  if (!entry) return null;
  return decodeURIComponent(entry.split("=").slice(1).join("="));
}

export const SentinelLogger = () => {
  const queue = useRef<LogQueueItem[]>([]);
  const processing = useRef(false);
  const disabled = useRef(false);
  const disabledUntil = useRef(0);
  const failureCount = useRef(0);

  const shouldIgnoreMessage = (value: unknown) => {
    const text = String(value || "");
    return (
      text.includes("/api/v1/system/client-log") ||
      text.includes("/api/v1/errors/log") ||
      text.includes("/api/v1/notifications/unread-count") ||
      text.includes("401 (Unauthorized)") ||
      text.includes("403 (Forbidden)")
    );
  };

  const isTemporarilyDisabled = () => Date.now() < disabledUntil.current;

  const scheduleBackoff = () => {
    failureCount.current += 1;
    const backoffMs = Math.min(300000, 1000 * 2 ** failureCount.current);
    disabledUntil.current = Date.now() + backoffMs;
  };

  const resetBackoff = () => {
    failureCount.current = 0;
    disabledUntil.current = 0;
  };

  // Send collected logs to backend
  const flushLogs = async () => {
    if (
      disabled.current ||
      isTemporarilyDisabled() ||
      queue.current.length === 0 ||
      processing.current
    )
      return;

    processing.current = true;
    const batch = [...queue.current];
    queue.current = []; // Clear local queue

    try {
      // Send each log individually to ensure atomic visibility in terminal
      // (Batching could be an optimization later, but we want 1-to-1 mapping for now)
      for (const log of batch) {
        const csrfToken = readCookie("csrf_token");
        const response = await fetch(ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
          },
          body: JSON.stringify(log),
          credentials: "same-origin",
        });
        if (response.status === 401 || response.status === 403) {
          scheduleBackoff();
          queue.current = [];
          break;
        }
      }
      resetBackoff();
    } catch {
      scheduleBackoff();
    } finally {
      processing.current = false;
    }
  };

  // flushLogs only uses refs; re-subscribing handlers on every render is unnecessary.
  useEffect(() => {
    let teardown: (() => void) | undefined;
    let cancelled = false;

    // 1. Intercept Global Errors
    const handleGlobalError = (event: ErrorEvent) => {
      if (disabled.current || isTemporarilyDisabled()) return;
      if (
        shouldIgnoreMessage(event.message) ||
        shouldIgnoreMessage(event.error?.message)
      ) {
        return;
      }
      queue.current.push({
        type: "ERROR",
        message: event.message,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
      });
      flushLogs();
    };

    // 2. Intercept Unhandled Promise Rejections
    const handlePromiseRejection = (event: PromiseRejectionEvent) => {
      if (disabled.current || isTemporarilyDisabled()) return;
      if (shouldIgnoreMessage(event.reason?.message || event.reason)) return;
      queue.current.push({
        type: "ERROR",
        message: `Unhandled Promise: ${event.reason}`,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        stack: event.reason?.stack,
      });
      flushLogs();
    };

    // 3. Optional: Intercept console.error (use only in development)
    const originalConsoleError = console.error;
    if (process.env.NODE_ENV === "development") {
      console.error = (...args: unknown[]) => {
        if (disabled.current) {
          originalConsoleError.apply(console, args);
          return;
        }
        // Call original first so developer still sees it in DevTools
        originalConsoleError.apply(console, args);

        // Convert args to string
        const message = args
          .map((arg) => (arg instanceof Error ? arg.message : String(arg)))
          .join(" ");
        if (shouldIgnoreMessage(message)) {
          return;
        }

        const stack = args.find((arg) => arg instanceof Error)?.stack;

        queue.current.push({
          type: "ERROR",
          message: `[Console] ${message}`,
          stack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
        });
        // Throttle console logs slightly
        setTimeout(flushLogs, 500);
      };
    }

    const attach = () => {
      if (cancelled) return;
      window.addEventListener("error", handleGlobalError);
      window.addEventListener("unhandledrejection", handlePromiseRejection);
      teardown = () => {
        window.removeEventListener("error", handleGlobalError);
        window.removeEventListener(
          "unhandledrejection",
          handlePromiseRejection
        );
        if (process.env.NODE_ENV === "development") {
          console.error = originalConsoleError; // Restore
        }
      };
    };

    let cancelIdle: (() => void) | undefined;
    const browserWindow = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          _callback: IdleRequestCallback,
          _options?: IdleRequestOptions
        ) => number;
        cancelIdleCallback?: (_handle: number) => void;
      };
    if (typeof browserWindow.requestIdleCallback === "function") {
      const idleId = browserWindow.requestIdleCallback(attach, {
        timeout: 3000,
      });
      cancelIdle = () => browserWindow.cancelIdleCallback?.(idleId);
    } else {
      const timeoutId = browserWindow.setTimeout(attach, 1500);
      cancelIdle = () => browserWindow.clearTimeout(timeoutId);
    }

    return () => {
      cancelled = true;
      cancelIdle?.();
      teardown?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // Headless component
};
